/**
 * useSelectionBox.ts
 * Hook for managing drag-to-select rectangle logic
 * 
 * Returns:
 * - selectionBox: { x, y, width, height } or null
 * - isSelecting: boolean
 * - handlers: onMouseDown, onMouseMove, onMouseUp
 */

import { useState, useCallback, useRef } from 'react';

export interface SelectionBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface UseSelectionBoxOptions {
  onSelectionComplete?: (box: SelectionBox) => void;
  minSize?: number; // Minimum selection size to be valid
}

export function useSelectionBox(options: UseSelectionBoxOptions = {}) {
  const { onSelectionComplete, minSize = 10 } = options;

  const [selectionBox, setSelectionBox] = useState<SelectionBox | null>(null);
  const [isSelecting, setIsSelecting] = useState(false);
  
  // Store start position
  const startPosRef = useRef<{ x: number; y: number } | null>(null);

  const onMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    // Only left click
    if (e.button !== 0) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    startPosRef.current = { x, y };
    setSelectionBox({ x, y, width: 0, height: 0 });
    setIsSelecting(true);
  }, []);

  const onMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!isSelecting || !startPosRef.current) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const currentX = e.clientX - rect.left;
    const currentY = e.clientY - rect.top;

    const startX = startPosRef.current.x;
    const startY = startPosRef.current.y;

    // Calculate box (handle dragging in any direction)
    const x = Math.min(startX, currentX);
    const y = Math.min(startY, currentY);
    const width = Math.abs(currentX - startX);
    const height = Math.abs(currentY - startY);

    setSelectionBox({ x, y, width, height });
  }, [isSelecting]);

  const onMouseUp = useCallback(() => {
    if (!isSelecting || !selectionBox) {
      setIsSelecting(false);
      return;
    }

    setIsSelecting(false);
    startPosRef.current = null;

    // Check minimum size
    if (selectionBox.width >= minSize && selectionBox.height >= minSize) {
      onSelectionComplete?.(selectionBox);
    }

    setSelectionBox(null);
  }, [isSelecting, selectionBox, minSize, onSelectionComplete]);

  const reset = useCallback(() => {
    setSelectionBox(null);
    setIsSelecting(false);
    startPosRef.current = null;
  }, []);

  return {
    selectionBox,
    isSelecting,
    handlers: {
      onMouseDown,
      onMouseMove,
      onMouseUp,
      onMouseLeave: onMouseUp, // Cancel if mouse leaves the area
    },
    reset,
  };
}
